import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../api';
import { t, tx } from '../../i18n';
import { fmtDate, fmtKB } from '../../lib/format';
import { eurC } from '../../lib/money';
import { useApi } from '../../lib/useApi';
import { Preview } from '../belege/BelegSheet';
import { Sheet } from '../belege/Sheet';
import { useCloseSheet } from '../lohn/Pages';
import type { FilingDetail } from './types';
import { ShareFile } from '../../components/ShareFile';

// Übertragungsprotokoll einer UStVA (?ustva=<id>): Vorschau, Angaben und Herunterladen. Bei einer Berichtigung
// steht das neueste Protokoll oben, die früheren darunter.
export function ProtokollSheetHost() {
  const [params] = useSearchParams();
  const close = useCloseSheet('ustva');
  const id = params.get('ustva');
  return id ? <ProtokollSheet key={id} id={id} onClose={close} /> : null;
}

function ProtokollSheet({ id, onClose }: { id: string; onClose(): void }) {
  const res = useApi<FilingDetail>(`/api/app/ustva/${id}`, { live: ['fristen'] });
  const [shown, setShown] = useState<string | null>(null);
  const f = res.data;

  // Meldung dazu gilt als gelesen, sobald das Fenster das Protokoll zeigt
  const loaded = !!f;
  useEffect(() => {
    if (loaded) api.post(`/api/app/ustva/${id}/read`).catch(() => {});
  }, [id, loaded]);

  if (!f) {
    return (
      <Sheet title={t('Übermittlungsprotokoll')} onClose={onClose}>
        {res.error ? <p className="meta">{res.error}</p> : <div className="spinner" aria-label={t('Lädt')} />}
      </Sheet>
    );
  }

  const files = [...f.files].reverse();
  const file = files.find((x) => x.id === shown) ?? files[0];
  const url = file ? `/api/app/ustva/${f.id}/file/${file.id}` : '';
  const refund = f.amount !== null && f.amount < 0;
  return (
    <Sheet title={t('Übermittlungsprotokoll')} onClose={onClose}>
      <div className="meta">
        {f.clientName} · {tx(f.title)} · {t('hochgeladen von Ihrer Kanzlei')}
      </div>
      {file && <Preview key={file.id} url={url} mime={file.mime} name={file.name} />}
      <dl className="akte ptbl">
        <div>
          <dt>{t('Zeitraum')}</dt>
          <dd>{tx(f.title)}</dd>
        </div>
        <div>
          <dt>{t('Steuernummer')}</dt>
          <dd>{f.taxNumber || '–'}</dd>
        </div>
        <div>
          <dt>{t('Übermittelt am')}</dt>
          <dd>
            {fmtDate(f.filedOn)}
            {f.corrected ? ` (${t('Berichtigung')})` : ''}
          </dd>
        </div>
        <div>
          <dt>{t('Fällig am')}</dt>
          <dd>{fmtDate(f.due)}</dd>
        </div>
        <div>
          <dt>{refund ? t('Erstattung') : t('Zahllast')}</dt>
          <dd>
            {f.amount !== null ? t('{amount} laut Protokoll', { amount: eurC(Math.abs(f.amount)) }) : t('steht im Protokoll')}
          </dd>
        </div>
      </dl>
      {files.length > 1 && (
        <div className="files pt-files">
          <h3>{t('Protokolle ({n})', { n: files.length })}</h3>
          {files.map((x) => (
            <button key={x.id} type="button" className="file" aria-pressed={x.id === file?.id} onClick={() => setShown(x.id)}>
              <span>
                {x.name}
                <small className="fsub">
                  {x.correction ? t('Berichtigung') : t('Erstübermittlung')} ·{' '}
                  {t('übermittelt am {date}', { date: fmtDate(x.filedOn) })} · {fmtKB(x.size)}
                </small>
              </span>
              <span className={x.id === file?.id ? 'ok' : 'chev'}>
                {x.id === file?.id ? t('angezeigt') : `${t('Ansehen')} ›`}
              </span>
            </button>
          ))}
        </div>
      )}
      {file && (
        <div className="row">
          <a className="btn2 dark" href={`${url}?download=1`} download>
            {files.length > 1
              ? t('„{name}“ herunterladen', { name: file.name })
              : file.mime === 'application/pdf'
                ? t('PDF herunterladen')
                : t('Bild herunterladen')}
          </a>
          <ShareFile url={`${url}?download=1`} name={file.name} />
        </div>
      )}
    </Sheet>
  );
}
